import React, { useMemo } from 'react';
import { useI18n } from 'next-localization';
import { useSitecoreContext } from 'hooks/useSitecoreContext';
import { useSelector } from 'react-redux';
import { getFundDetailsPageUrl } from '@/store/fundCentre';
import path from 'path';
import ScLink from 'components/Generic/SitecoreWrappers/ScLink';
import FundsTableRowSkeleton from 'components/Pages/FundsCenter/AllFunds/FundsListing/FundsTableParts/FundsTableRowSkeleton';
import Tooltip from 'components/Generic/Tooltip';
import FundsExternalLink from 'components/Pages/FundsCenter/AllFunds/FundsListing/FundsTableParts/CellParts/FundsExternalLink';
import { FactsheetType } from 'model/FundCentre';
import FundsShareclassCell from 'components/Pages/FundsCenter/AllFunds/FundsListing/FundsTableParts/CellParts/Cells/FundsShareclassCell';
import FundsPrice from 'components/Pages/FundsCenter/AllFunds/FundsListing/FundsTableParts/CellParts/FundsPrice';

import styles from '../../../styles/FundsTableRow.module.scss';
import sanitizeURL from 'lib/utils/sanitizeUrl';

type ShareclassType = {
  factsheet: FactsheetType;
  isin: string;
  kiid: any;
  shareclassID: string;
  shareclassName: string;
  url: string;
  [key: string]: any;
};

type FundType = {
  assetClass: string;
  correlationFundRangeId: string;
  id: string;
  name: string;
  sfdrClassification: string;
  selectedShareclass: ShareclassType;
  shareclasses: ShareclassType[];
  skeleton?: any;
  [key: string]: any;
};

type ColumnsToShowType = {
  general: {
    propertyName: string;
    visibleName: string;
  }[];
  shareclassSpecific: {
    propertyName: string;
    visibleName: string;
  }[];
};

type Props = {
  fund: FundType;
  fundIdx: number;
  columnsToShow: ColumnsToShowType;
  hideShareclass: boolean;
};

const FundsTableRow: React.FC<Props> = ({ fund, fundIdx, hideShareclass, columnsToShow }) => {
  const { sitecoreContext } = useSitecoreContext();
  const { t } = useI18n();
  const notNumericValues: string[] = ['currency', 'newspaperName', 'ticker'];
  const PRICE_TYPE_COLUMN: string[] = [
    'dailyPrice',
    'priceChange',
    'navPrice',
    'applicationPrice',
    'redemptionPrice',
    'nav',
    'offerPrice',
    'bidPrice',
    'assetSize',
    'sharePrice',
    'estimatedCumFairNav',
    'estimatedExFairNav',
    'cumFairPremiumDiscount',
    'exFairPremiumDiscount',
    'navChange',
  ];
  const selectedShareclassUrl: string = fund.selectedShareclass?.url || '';
  const fundDetailsPageUrl = useSelector(getFundDetailsPageUrl);

  const hasValidUrlData = useMemo(() => {
    return !!fundDetailsPageUrl && !!selectedShareclassUrl;
  }, [fundDetailsPageUrl, selectedShareclassUrl]);

  const isAbsoluteLink = useMemo(() => {
    return selectedShareclassUrl.startsWith('http');
  }, [selectedShareclassUrl]);

  const fundUrl = useMemo(() => {
    const encodedURL = sanitizeURL(path.join(fundDetailsPageUrl, selectedShareclassUrl));
    return isAbsoluteLink ? selectedShareclassUrl : encodedURL;
  }, [fundDetailsPageUrl, isAbsoluteLink, selectedShareclassUrl]);

  const linkValue = useMemo(() => {
    return {
      value: { href: fundUrl, text: fund?.name, target: isAbsoluteLink ? '_blank' : '' },
    };
  }, [fundUrl, fund, isAbsoluteLink]);

  const isPrice = (propertyName: string) => {
    return PRICE_TYPE_COLUMN.includes(propertyName);
  };

  const hasDocumentURI = (propertyName: string) => {
    return fund.selectedShareclass[propertyName]?.documentURI;
  };

  if (fund.skeleton) {
    return <FundsTableRowSkeleton />;
  } else {
    return (
      <tr className={styles.fundsTableRow}>
        <td className="text-link sticky-column">
          {hasValidUrlData && !sitecoreContext.pageEditing ? (
            <ScLink className="text-decoration-underline" field={linkValue} />
          ) : (
            <span>{fund.name}</span>
          )}
        </td>
        {!hideShareclass && (
          <FundsShareclassCell
            key={fund.selectedShareclass ? fund.selectedShareclass.isin : 'default-key'}
            fund={fund}
            fundIdx={fundIdx}
          />
        )}
        {columnsToShow.general.map((generalColumn) => (
          <td key={generalColumn.visibleName}>
            <div className="d-flex">
              <span className="d-xl-none section-header-small">{generalColumn.visibleName}</span>
              {generalColumn.propertyName === 'sfdrClassification' &&
                t('fundsFundCentreTooltipSfdr') &&
                t('fundsFundCentreTooltipSfdr') !== 'fundsFundCentreTooltipSfdr' && (
                  <Tooltip
                    arialLabelText={generalColumn.visibleName}
                    className="d-xl-none"
                    tooltipTitle={generalColumn.visibleName}
                    tooltipContent={t('fundsFundCentreTooltipSfdr')}
                  />
                )}
            </div>

            <span className="h6">{fund[generalColumn.propertyName]}</span>
          </td>
        ))}
        {columnsToShow.shareclassSpecific.map((specificColumn) => (
          <td key={specificColumn.propertyName}>
            <span className="d-xl-none section-header-small">
              {specificColumn.visibleName}
              {specificColumn.propertyName === 'nav' &&
                t('fundsFundCentreTooltipNav') &&
                t('fundsFundCentreTooltipNav') !== 'fundsFundCentreTooltipNav' && (
                  <Tooltip
                    arialLabelText={specificColumn.visibleName}
                    className="d-xl-none"
                    tooltipTitle={specificColumn.visibleName}
                    tooltipContent={t('fundsFundCentreTooltipNav')}
                  />
                )}
            </span>
            {fund.selectedShareclass && (
              <>
                {isPrice(specificColumn.propertyName) && (
                  <FundsPrice
                    selectedShareclass={fund.selectedShareclass}
                    propertyName={specificColumn.propertyName}
                  />
                )}
                {notNumericValues.includes(specificColumn.propertyName) && (
                  <>
                    {fund.selectedShareclass[specificColumn.propertyName] ? (
                      <div className="h6">
                        {fund.selectedShareclass[specificColumn.propertyName]}
                      </div>
                    ) : (
                      <div className="h6">--</div>
                    )}
                  </>
                )}
                {hasDocumentURI(specificColumn.propertyName) && (
                  <FundsExternalLink
                    document={fund.selectedShareclass[specificColumn.propertyName]}
                  />
                )}
              </>
            )}
          </td>
        ))}
      </tr>
    );
  }
};

export default FundsTableRow;
